import classNames from 'classnames';
import { defaultSchedulingColors } from 'containers/scheduling/constants/constants';
import { useSchedulingSettings } from 'containers/scheduling/context/SchedulingSettingsContext';
import EmployeeCountIcon from 'containers/scheduling/EmployeeCountIcon/EmployeeCountIcon';
import { useCalendarView } from 'containers/scheduling/hooks';
import { Fragment, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { IMember } from 'types';
import ICostCode from 'types/CostCode';
import IEquipment from 'types/Equipment';
import IProject from 'types/Project';
import { getFormattedPathFromProject } from 'utils/projectUtils';
import { getCostCodeDisplay, getEquipmentDisplay, getFullNameFromMember } from 'utils/stringUtils';
import { calculateTextColorForBackground } from 'utils/styleUtils';
import SchedulingEventContainer from '../SchedulingEventContainer/SchedulingEventContainer';

function EmployeeCountRow({
  children,
  memberCount,
  reminderColor,
}: {
  children: ReactNode;
  memberCount: number;
  reminderColor: string | null | undefined;
}) {
  if (memberCount < 2) {
    return <>{children}</>;
  }

  const backgroundColor = reminderColor ? reminderColor : defaultSchedulingColors.work;
  const iconColor = calculateTextColorForBackground(backgroundColor);

  return (
    <div className="employee-count-row">
      {children}
      <EmployeeCountIcon memberCount={memberCount} theme={iconColor ?? 'light'} />
    </div>
  );
}

export interface ISchedulingEventDetailsProps {
  time: string;
  color: string;
  type: 'work' | 'break';
  members: IMember[];
  project: IProject | null | undefined;
  costCode: ICostCode | null | undefined;
  equipment: IEquipment | null | undefined;
  className?: string | object;
}

function SchedulingEventDetails({
  time,
  color,
  type,
  members,
  project,
  costCode,
  equipment,
  className,
}: ISchedulingEventDetailsProps) {
  const [t] = useTranslation();
  const projectTitle = getFormattedPathFromProject(project, true);

  const { settings } = useSchedulingSettings();
  const view = useCalendarView();

  const viewSettings = settings[view];

  function getElements() {
    const elements = [];
    let firstRendered = false;
    if (viewSettings.time) {
      firstRendered = true;
      elements.push(
        <EmployeeCountRow key="time" reminderColor={color} memberCount={members.length}>
          <div className="ellipsis">{time}</div>
        </EmployeeCountRow>
      );
    }

    if (viewSettings.employees) {
      const employeeField = (
        <div className="ellipsis" key="employee">
          {members.map(getFullNameFromMember).join(', ')}
        </div>
      );
      if (firstRendered) {
        elements.push(employeeField);
      } else {
        firstRendered = true;
        elements.push(
          <EmployeeCountRow key="employee" reminderColor={color} memberCount={members.length}>
            {employeeField}
          </EmployeeCountRow>
        );
      }
    }

    if (viewSettings.project && type === 'work') {
      const projectField = (
        <div className="ellipsis" key="project">
          {projectTitle !== '' ? projectTitle : t('No Project')}
        </div>
      );
      if (firstRendered) {
        elements.push(projectField);
      } else {
        firstRendered = true;
        elements.push(
          <EmployeeCountRow key="project" reminderColor={color} memberCount={members.length}>
            {projectField}
          </EmployeeCountRow>
        );
      }
    }

    if (viewSettings.costCode && type === 'work') {
      const costCodeField = (
        <div className="ellipsis" key="costCode">
          {getCostCodeDisplay(costCode)}
        </div>
      );
      if (firstRendered) {
        elements.push(costCodeField);
      } else {
        firstRendered = true;
        elements.push(
          <EmployeeCountRow key="costCode" reminderColor={color} memberCount={members.length}>
            {costCodeField}
          </EmployeeCountRow>
        );
      }
    }

    if (viewSettings.equipment && type === 'work') {
      const equipmentField = (
        <div className="ellipsis" key="equipment">
          {getEquipmentDisplay(equipment)}
        </div>
      );
      if (firstRendered) {
        elements.push(equipmentField);
      } else {
        firstRendered = true;
        elements.push(
          <EmployeeCountRow key="equipment" reminderColor={color} memberCount={members.length}>
            {equipmentField}
          </EmployeeCountRow>
        );
      }
    }

    if (type === 'break') {
      if (firstRendered) {
        elements.push(<Fragment key="break">{t('Break')}</Fragment>);
      } else {
        elements.push(
          <EmployeeCountRow key="break" reminderColor={color} memberCount={members.length}>
            {t('Break')}
          </EmployeeCountRow>
        );
      }
    }

    return elements;
  }

  const classes = classNames('scheduling-event-details', className);

  return <SchedulingEventContainer className={classes}>{getElements()}</SchedulingEventContainer>;
}

export default SchedulingEventDetails;
